import React, { useState } from 'react';
import ExpandableBio from '../ui-components/ExpandableBio.jsx';
import { Clock4, ChevronDown, MapPin, Mic, GraduationCap } from 'lucide-react';
import { groupSubsessionsByTime, isDisplayableRange } from '../utils/helpers.js';
import { sortSubsessions } from '../utils/sort-subsessions.js';
import SpeakerCard from './SpeakerCard.jsx';
import AddToCalendar from '../shared/AddToCalendar.jsx';
import SponsoredBy from '../shared/SponsoredBy.jsx';
import { useI18n } from '../i18n/index.jsx';
import { groupSpeakersBySlug, roleGroupHeading } from '../utils/group-speakers-by-role.js';
import ceuCreditsLabel from '../utils/ceu-credits-label.js';

const renderTagSection = (session, t) => {
  const tags = [];

  if (session.format) {
    tags.push({ type: 'format', icon: '#2F54EB', label: t('tag_format'), values: [session.format] });
  }
  if (session.language) {
    tags.push({ type: 'language', icon: '#CC782F', label: t('tag_language'), values: [session.language] });
  }
  if (session.level) {
    tags.push({ type: 'level', icon: '#006FD6', label: t('tag_level'), values: [session.level] });
  }
  if (session.track) {
    tags.push({ type: 'track', icon: '#2F54EB', label: t('tag_track'), values: [session.track] });
  }
  if (session.tags && session.tags.length > 0) {
    tags.push({
      type: 'tags',
      icon: '#722ED1',
      label: t('tag_tag'),
      values: session.tags.map(tag => tag.name),
      isTagChips: true,
    });
  }
  if (tags.length === 0) {
    return null;
  }
  return (
    <div className="schedule-itinerary__tag-fields">
      {tags.map((tagGroup, index) => (
        <div key={index} className="schedule-itinerary__tag-group">
          <div className="schedule-itinerary__tag-label">
            <svg className="schedule-itinerary__tag-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M11.4201 3.89398C11.1801 3.55398 10.7801 3.33398 10.3334 3.33398L3.00008 3.34065C2.26675 3.34065 1.66675 3.93398 1.66675 4.66732V11.334C1.66675 12.0673 2.26675 12.6607 3.00008 12.6607L10.3334 12.6673C10.7801 12.6673 11.1801 12.4473 11.4201 12.1073L14.3334 8.00065L11.4201 3.89398ZM10.3334 11.334H3.00008V4.66732H10.3334L12.7001 8.00065L10.3334 11.334Z"
                fill={tagGroup.icon}
              />
            </svg>
            <span style={{ color: tagGroup.icon }}>{tagGroup.label}</span>
          </div>
          <div className="schedule-itinerary__tag-values">
            {tagGroup.isTagChips ? (
              tagGroup.values.map((value, i) => (
                <span key={i} className="schedule-itinerary__tag-chip">
                  {value}
                </span>
              ))
            ) : (
              <span className="sb-dot-list">
                {tagGroup.values.map((value, i) => (
                  <span key={i} className="sb-dot-list__item">
                    {value}
                  </span>
                ))}
              </span>
            )}
          </div>
          {index < tags.length - 1 && <div className="schedule-itinerary__tag-divider"></div>}
        </div>
      ))}
    </div>
  );
};

const SessionCard = ({
  session,
  onSessionClick,
  onSpeakerClick,
  onSubsessionClick,
  showAddToCalendar = false,
  showSessionSponsors = false,
  sessionForCalendar = {},
  //   visibleSessionFields = [],
  visibleSpeakerFields = [],
  // SB-10242: grouped-field maps (by internal_name) that may carry a custom CEU label.
  labelFieldsById = [],
  isHighlighted = false,
}) => {
  const [expandedSessions, setExpandedSessions] = useState(new Set(['3']));
  const isExpandedSession = expandedSessions.has(session.id);
  const { t } = useI18n();
  const subSessionsByTime = groupSubsessionsByTime(
    sortSubsessions(session?.subsessions || []),
    session.timezone
  );

  const toggleSessionExpansion = sessionId => {
    const newExpandedSessions = new Set(expandedSessions);
    if (newExpandedSessions.has(sessionId)) {
      newExpandedSessions.delete(sessionId);
    } else {
      newExpandedSessions.add(sessionId);
    }
    setExpandedSessions(newExpandedSessions);
  };

  if (!session) return null;

  const handleSessionClick = () => {
    if (onSessionClick && session.id) {
      onSessionClick(session.id);
    }
  };

  const handleSpeakerClick = speakerId => {
    if (onSpeakerClick && speakerId) {
      onSpeakerClick(speakerId);
    }
  };

  // SB-8853: slug-grouped participants with organizer labels.
  const speakerGroups = groupSpeakersBySlug(session.speakers);

  return (
    <div
      className={`schedule-itinerary__session-card${
        isHighlighted ? ' schedule-itinerary__session-card--highlighted' : ''
      }`}
      data-session-id={session.id}
    >
      <div className="schedule-itinerary__session-content">
        <div className="schedule-itinerary__session-header">
          {session.track && (
            <div
              className="schedule-itinerary__session-badge"
              style={{ backgroundColor: session.track_color || '#1B6EC2' }}
            >
              {session.track}
            </div>
          )}
          <h3 className="schedule-itinerary__session-title" onClick={handleSessionClick}>
            {session.title}
          </h3>
        </div>

        {session.description && (
          <div className="schedule-itinerary__session-description">
            <ExpandableBio
              htmlContent={session.description}
              maxLength={300}
              className="schedule-itinerary__session-description-text"
            />
          </div>
        )}

        <div className="schedule-itinerary__session-details">
          {isDisplayableRange(session.range) ? (
            <div className="schedule-itinerary__detail-item">
              <Clock4 size={16} />
              <span>{session.range}</span>
            </div>
          ) : null}
          {session.location && (
            <div className="schedule-itinerary__detail-item">
              <MapPin size={16} />
              <span>{session.location}</span>
            </div>
          )}
          {session.ceu_credits && (
            <div className="schedule-itinerary__detail-item">
              <GraduationCap size={16} />
              <span>
                {ceuCreditsLabel(labelFieldsById, t)} {session.ceu_credits}
              </span>
            </div>
          )}
          {showAddToCalendar && (
            <div className="schedule-itinerary__detail-item">
              <AddToCalendar sessionData={sessionForCalendar} enabled={showAddToCalendar} />
            </div>
          )}
        </div>
      </div>

      {session.speakers?.length > 0 && speakerGroups.length > 0 && (
        <div className="schedule-itinerary__speakers-section">
          {speakerGroups.map(group => (
            <div key={group.slug} className="schedule-itinerary__speaker-group">
              <h4 className="schedule-itinerary__speaker-group-title">{roleGroupHeading(t, group)}</h4>
              <div className="schedule-itinerary__speaker-list">
                {group.speakers.map(speaker => (
                  <SpeakerCard key={speaker.id} speaker={speaker} onSpeakerClick={handleSpeakerClick} visibleSpeakerFields={visibleSpeakerFields} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showSessionSponsors && <SponsoredBy sponsors={session?.sponsors} />}

      {renderTagSection(session, t)}

      {session?.subsessions?.length > 0 && (
        <div className="schedule-itinerary__view-details">
          <button className="schedule-itinerary__view-details-btn" onClick={() => toggleSessionExpansion(session.id)}>
            <span>{isExpandedSession ? t('hide_details') : t('view_details')}</span>
            <ChevronDown size={14} />
          </button>
        </div>
      )}

      {/* Sub-sessions (if expanded) */}
      {isExpandedSession && session?.subsessions?.length > 0 && (
        <div className="schedule-itinerary__sub-sessions">
          {/* Group subsessions by time */}
          {Object.entries(subSessionsByTime).map(([time, subSessions]) => (
            <div key={time || '__untimed__'} className="schedule-itinerary__sub-session-time-group">
              {isDisplayableRange(time) ? (
                <div className="schedule-itinerary__sub-session-time-header">
                  <div className="schedule-itinerary__sub-session-time-label">
                    <Clock4 size={16} />
                    <span>{time}</span>
                  </div>
                </div>
              ) : null}
              <div className="schedule-itinerary__sub-session-cards">
                {subSessions.map((subSession, index) => {
                  // SB-8853: show every payload participant — the API already
                  // gated by the embed's selected roles; the old speaker-only
                  // filter hid moderators/chairs/custom roles.
                  const speakers = subSession.speakers || [];
                  return (
                    <div
                      key={index}
                      className={`schedule-itinerary__sub-session-card${
                        onSubsessionClick ? ' schedule-itinerary__sub-session-card--clickable' : ''
                      }`}
                      onClick={onSubsessionClick ? () => onSubsessionClick(subSession.id) : undefined}
                      role={onSubsessionClick ? 'button' : undefined}
                      tabIndex={onSubsessionClick ? 0 : undefined}
                      onKeyDown={
                        onSubsessionClick
                          ? event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSubsessionClick(subSession.id);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="schedule-itinerary__sub-session-content">
                        <h5 className="schedule-itinerary__sub-session-title">{subSession.title}</h5>
                        <div className="schedule-itinerary__sub-session-details">
                          {isDisplayableRange(subSession.range) ? (
                            <div className="schedule-itinerary__sub-session-time-range">
                              <Clock4 size={14} />
                              <span>{subSession.range}</span>
                            </div>
                          ) : null}
                          {speakers.length > 0 && (
                            <div className="schedule-itinerary__sub-session-speakers">
                              <Mic size={14} />
                              <span className="sb-dot-list">
                                {speakers.map((speaker, i) => (
                                  <span key={speaker.id ?? i} className="sb-dot-list__item">
                                    {speaker.full_name}
                                  </span>
                                ))}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionCard;
